import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, refreshSession } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      try {
        const { data } = await refreshSession();
        setUser(data);
      } catch {
        setUser(false);
      }
    }
  }, []);

  useEffect(() => {
    loadMe();
    const onExpired = () => setUser(false);
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, [loadMe]);

  useEffect(() => {
    if (!user) {
      return undefined;
    }
    const t = setInterval(() => { refreshSession().catch(() => setUser(false)); }, 10 * 60 * 1000);
    return () => clearInterval(t);
  }, [user]);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    if (data.mfa_required) return data;
    setUser(data);
    return data;
  };

  const verifyMfa = async (mfa_token, code) => {
    const { data } = await api.post("/auth/mfa", { mfa_token, code });
    setUser(data);
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  return <AuthContext.Provider value={{ user, setUser, login, verifyMfa, logout, reload: loadMe }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
